// src/store/slices/employeeEducationBackgroundSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_BASE_URL;

/* ===========================
    NORMALIZER
=========================== */
const normalizeEducation = (edu) => ({
  id: edu.id,
  employeeId: edu.employee?.id || null,
  level: edu.level || "",
  schoolName: edu.schoolName || "",
  schoolAddress: edu.schoolAddress || "",
  yearFrom: edu.yearFrom || null,
  yearTo: edu.yearTo || null,
  course: edu.course || "",
  file_url: edu.file_url || null, // Added file_url to normalizer
  createdAt: edu.createdAt,
  updatedAt: edu.updatedAt,
});

/* ===========================
    THUNKS
=========================== */

// Fetch by employee ID
export const fetchEducationByEmployeeId = createAsyncThunk(
  "employeeEducation/fetchByEmployeeId",
  async (employeeId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${API_URL}/employee/education-background/employee/${employeeId}`
      );
      if (!Array.isArray(res.data)) return [];
      return res.data.map(normalizeEducation);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Fetch single record by ID
export const fetchEducationById = createAsyncThunk(
  "employeeEducation/fetchById",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axios.get(`${API_URL}/employee/education-background/${id}`);
      return normalizeEducation(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Create
export const createEducationBackground = createAsyncThunk(
  "employeeEducation/create",
  async (data, { rejectWithValue }) => {
    try {
      const res = await axios.post(`${API_URL}/employee/education-background`, data);
      return normalizeEducation(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Update
export const updateEducationBackground = createAsyncThunk(
  "employeeEducation/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await axios.put(`${API_URL}/employee/education-background/${id}`, data);
      return normalizeEducation(res.data);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

/**
 * Upload Education File (Mirroring Government Doc Pattern)
 * 1. Uploads to generic file service
 * 2. Updates the education record with the returned URL
 */
export const uploadEducationFile = createAsyncThunk(
  "employeeEducation/uploadFile",
  async ({ id, file, companyId }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", file);

      // Now hitting the specialized Education endpoint instead of the generic one
      const response = await axios.post(
        `${API_URL}/employee/education-background/${id}/upload?companyId=${companyId}`,
        formData,
        { 
          headers: { 
            "Content-Type": "multipart/form-data" 
          } 
        }
      );

      // The backend returns the updated EducationBackground object
      return { 
        url: response.data.file_url, 
        id: response.data.id 
      };
    } catch (err) {
      // Direct access to the error message from the backend
      return rejectWithValue(
        err.response?.data?.message || "File upload and record update failed"
      );
    }
  }
);

// Delete
export const deleteEducationBackground = createAsyncThunk(
  "employeeEducation/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${API_URL}/employee/education-background/${id}`);
      return id;
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Check Employee Completeness
export const checkEmployeeCompleteness = createAsyncThunk(
  "employeeEducation/checkCompleteness",
  async (employeeId, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        `${API_URL}/employee/${employeeId}/check-completeness`
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Completeness check failed");
    }
  }
);

/* ===========================
    SLICE
=========================== */

const initialState = {
  list: [],
  selected: null,
  status: "idle",
  completenessStatus: "idle",
  error: null,
};

const employeeEducationBackgroundSlice = createSlice({
  name: "employeeEducation",
  initialState,
  reducers: {
    clearSelectedEducation: (state) => {
      state.selected = null;
      state.status = "idle";
      state.completenessStatus = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEducationByEmployeeId.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = action.payload;
      })
      .addCase(fetchEducationById.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.selected = action.payload;
      })
      .addCase(createEducationBackground.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list.push(action.payload);
      })
      .addCase(updateEducationBackground.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = state.list.map((e) =>
          e.id === action.payload.id ? action.payload : e
        );
        state.selected = action.payload;
      })
      /* Handle File Upload Fulfilled */
      .addCase(uploadEducationFile.fulfilled, (state, action) => {
        state.status = "succeeded";
        const index = state.list.findIndex((e) => e.id === action.payload.id);
        if (index !== -1) {
          state.list[index].file_url = action.payload.url;
        }
      })
      .addCase(deleteEducationBackground.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.list = state.list.filter((e) => e.id !== action.payload);
        if (state.selected?.id === action.payload) state.selected = null;
      })
      .addCase(checkEmployeeCompleteness.pending, (state) => {
        state.completenessStatus = "loading";
      })
      .addCase(checkEmployeeCompleteness.fulfilled, (state) => {
        state.completenessStatus = "succeeded";
      })
      .addCase(checkEmployeeCompleteness.rejected, (state, action) => {
        state.completenessStatus = "failed";
        state.error = action.payload;
      })
      .addMatcher(
        (action) => action.type.endsWith("/pending") && !action.type.includes("checkCompleteness"),
        (state) => {
          state.status = "loading";
          state.error = null;
        }
      )
      .addMatcher(
        (action) => action.type.endsWith("/rejected") && !action.type.includes("checkCompleteness"),
        (state, action) => {
          state.status = "failed";
          state.error = action.payload || action.error.message;
        }
      );
  },
});

export const { clearSelectedEducation } = employeeEducationBackgroundSlice.actions;
export default employeeEducationBackgroundSlice.reducer;